import Vue from 'vue';
import Component from 'vue-class-component';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import moment from 'moment';
require('focus-visible');

import {
  LoyaltyProfile,
  Promotion,
  CmsLink,
  CustomerProfile,
  DeliveryApiException,
  CustomerDeliveryAddress,
  OrderMealRequestEntree,
  TokenizedPaymentCard,
  UpsellItem,
} from '@/models';
import { HeaderOptions } from '@/store/ui';
import { getBrowserProduct } from '@/helpers';
import { slugify } from '@/helpers/formatting';
import { LoyaltyExplainerModalState } from '@/components/modals/CmgLoyaltyExplainerModal/cmg-loyalty-explainer-modal';
import { countries, RESIZE_LISTENER_POLL_RATE_MS, ui } from '@/constants';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { Routes } from '@/router';
import { EnrollmentOptions } from '@/services/loyalty/loyalty.service';
import { getCountryByHostname, PersistCrossDomainData } from '@/helpers/country';
import { MUTATIONS as MENU_MUTATIONS } from '@/store/menu';
import { MENU_METADATA_CHANNELS } from '@/services/metadata/metadata.service';
import { isDesktop } from '@/helpers/util';
import { getCountryCodeByTopLevelDomain } from '@/helpers/country';
import { AccountPageLoadEvent } from '@/services/analytics/events';
import { PageLoadEventType } from '@/services/analytics/platforms/adobe-analytics/models';
import { RestaurantEmbeds } from '@/services/restaurant/restaurant.service';
import { LoginCompleteEvent } from '@/services/analytics/events/login-complete-event';
import { USER_PROFILE_DECISION_SCOPES } from '@/services/analytics/platforms/adobe-analytics/constants';
import { getTopLevelMenuInteraction } from '@/services/analytics/platforms/adobe-analytics/helpers';
import { AemIdComponent, AEMComponents, AEMAppIdsToHide } from '@/models/aem';

@Component({
  name: 'cmg-app',
})
export default class CmgApp extends Vue {
  watchers: (() => any)[] = [];
  private isLoadingFavorites: boolean = false;

  get build(): string {
    return this.$services.config.getBuild();
  }

  get scrollable(): boolean {
    return this.$appStore.state.ui.app.scrollable;
  }

  get appIsBlurred(): boolean {
    return this.$appStore.state.ui.app.blurred;
  }

  get contentIsBlurred(): boolean {
    return this.$appStore.state.ui.app.contentBlurred;
  }

  get shouldShowPepper(): boolean {
    return this.$appStore.state.ui.app.pepperVisibility;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get giftCard(): TokenizedPaymentCard | undefined {
    return this.$appStore.state.customer.wallet.paymentCards.find(card => card.isGiftCard === true);
  }

  get isLoggedIn(): boolean | undefined {
    return this.$services.customer.isLoggedIn();
  }

  get customerLoyaltyProfile(): LoyaltyProfile | null {
    if (!this.$services.loyalty.isLoyaltyEligible(this.customer)) {
      return null;
    }

    return this.$services.loyalty.getLoyaltyProfile(this.customer!);
  }

  get customerPromotions(): Promotion[] {
    return this.$appStore.state.customer.wallet.promotions;
  }

  get headerState(): HeaderOptions {
    return this.$appStore.state.ui.header.options;
  }

  get headerLinks(): CmsLink[] | [] {
    return this.$appStore.state.cms.header.links;
  }

  get containerClasses(): object {
    const browser = this.$services.ui.getBrowserProduct();
    const browserDetectionClassName = `browser-is-${browser.toLowerCase()}`;

    return {
      scrollable: this.scrollable,
      [browserDetectionClassName]: true,
    };
  }

  get isIE(): boolean {
    return getBrowserProduct(navigator) === 'IE';
  }

  get isOnRewardsPages(): boolean {
    const rewardsRoutes = new Set([Routes.Rewards, Routes.RewardsExchange, Routes.RewardsPointsHistory]);
    return rewardsRoutes.has(this.$route.name as Routes);
  }

  get cmgAppContentId(): string {
    return ui.componentIds.cmgAppContent;
  }

  get siteCountryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get sessionFrameUrl(): string {
    return `${this.$services.config.getSessionFrameUrl()}/session.html`;
	}

  get isGroupOrderParticipant(): boolean {
    return this.$services.order.isCustomerParticipantOfGroupOrder();
  }

  get isFullPageLayout(): boolean {
    return this.$router.currentRoute.meta?.isFullPageLayout;
  }

  get isVendorPage(): boolean {
    return [Routes.VendorSignIn, Routes.VendorCreateAccount, Routes.VendorPasswordResetRequestPage].includes(this.$router.currentRoute.name as Routes);
  }

  get showSessionExpiredBanner(): boolean {
    return this.$store.state.ui.showSessionExpiredBanner;
  }

  get isPageOnAEMCS(): boolean {
    const isAemPage = AEMAppIdsToHide && AEMAppIdsToHide.length ? AEMAppIdsToHide.every( id => !!document.querySelector(id)) : false;
    const isAEMCS = window._cmgIsAEMCS ?? false;
    return isAemPage && (isAEMCS?? true);
  }

  get AEMComponents(): AemIdComponent[] {
    return AEMComponents;
  }

  private resizeListener?: () => void;

  async onLoadSessionIframe() {
    const iframe = (document.getElementById('sessionFrame') as unknown) as HTMLIFrameElement;
    if (iframe) {
      const win = iframe.contentWindow;

      window.onmessage = async (event: any) => {
        if (event.origin !== this.$services.config.getSessionFrameUrl()) {
          return;
        }

        if (win) {
          await this.handleCrossDomainDataMessage(event, win);
        }
      };

      // Send a message to the session iframe to return a stored jwt and customer data
      if (win) {
        win.postMessage(JSON.stringify({ key: 'cmg-vuex', method: 'get' }), this.$services.config.getSessionFrameUrl());
      }
    }
  }

  async mounted() {
    this.setTitleAndDescription();
    this.$services.ui.trackClientWidth(document.body.clientWidth);
    this.emitAdobeAnalyticsEventsAfterAdobeLaunchScriptHasLoaded();

    this.$el.parentNode?.insertBefore(document.createComment(`QA Build Reference: ${this.build}`), this.$el);

    this.resetMenuAndMetadataCachesOnce();

    this.$services.ui.setIsAemHomepageEnabled(this.isPageOnAEMCS);

    // If coming from cross-domain and not logged in clear jwt for the current domain
    if (this.$route.query.loggedIn) {
      const loggedIn = this.$route.query.loggedIn == 'true';
      const urlHash = this.getUrlHash();
      this.$router.replace({ query: omit(this.$route.query, 'loggedIn'), hash: urlHash });
      if (!loggedIn) {
        this.$services.customer.setJwt('');
      }
    }

    // this.watchers.push(this.$appStore.watch(state => state.ui.app.scrollable, this.setBodyScroll));
    this.watchers.push(this.$appStore.watch(state => state.customer.profile, this.onUserLogin));
    this.watchers.push(this.$appStore.watch(state => state.restaurant.mostRecentRestaurant, this.onFetchMostRecentRestaurant));
    this.watchers.push(this.$appStore.watch(state => state.restaurant.selectedRestaurant, this.onRestaurantSelected));

    this.resizeListener = debounce(() => {
      this.$services.ui.trackClientWidth(document.body.clientWidth);
      if (this.$services.utils.checkDidSwitchDesktopTablet()) {
        this.$services.adobeTarget.sendAdobeTargetExperienceRequest(this.$route.name);
      }
      this.setMetadataCache();

    }, RESIZE_LISTENER_POLL_RATE_MS);

    window.addEventListener('resize', this.resizeListener);
    
    // AEM Homepage START
    if( this.isPageOnAEMCS === false ) {
      AEMAppIdsToHide.map(id => document.querySelector(id)?.classList.add('d-none'));
    }

    // update main navigation href to /rewards instead of /order/rewards if user is not logged in
    if (this.isPageOnAEMCS) {
      this.watchers.push(this.$appStore.watch(state => state.customer.jwt, this.handleCustomerRewardsLink, { immediate: true }));
      window.CHIPW.ui.toggleSignInModal = this.toggleSignInModal;
    }

    this.selectRestaurantByURL();
    this.handleSpecialRoutes();

    // If user refreshes the page or navigates away and back, we must try to poll
    this.$services.order.pollGroupOrderIfExists();

    const countryProfile = getCountryByHostname(window.location.hostname);
    const aemCountryProfile = window._cmgCountryCode? countries[window._cmgCountryCode]: undefined;
    if(aemCountryProfile) {
      this.$services.ui.setCountryProfile(aemCountryProfile, false);
    }
    else if (countryProfile) {
      this.$services.ui.setCountryProfile(countryProfile, false);
    }

    const restaurant = this.$appStore.state.restaurant.selectedRestaurant;

    if (restaurant && restaurant.address.countryCode && this.$appStore.state.ui.countryProfile.countryCode.toLowerCase() !== restaurant.address.countryCode.toLowerCase()) {
      this.$services.restaurant.selectRestaurant(null, false);
      this.$services.order.setDeliveryAddress(null);
    }

    const scrollElement = document.getElementById(this.cmgAppContentId);
    if (scrollElement) {
      this.$services.googleAnalytics.trackElementScrollPercentage(scrollElement);
    }
  }

  destroyed() {
    for (const unsubFn of this.watchers) {
      unsubFn();
    }

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  private handleCustomerRewardsLink() {
    if(typeof this.$appStore.state.customer.jwt !== 'undefined' && this.$appStore.state.customer.jwt !== '') {
      const rewardsMenuLinks = document.querySelectorAll('a[href="/rewards"]');
        rewardsMenuLinks.forEach(rewardLink => {
          rewardLink.setAttribute('href', '/order/rewards');
        });
    } else{
      const rewardsMenuLinks = document.querySelectorAll('a[href="/order/rewards"]');
        rewardsMenuLinks.forEach(rewardLink => {
          rewardLink.setAttribute('href', '/rewards');
        });
    }
  }

  onSignIn() {
    const args = this.$appStore.state.ui.modals.signIn.args;
    if (args && args.route) {
      this.$router.push({ name: args.route });
    }

    this.$services.ui.setModalVisibility('signIn', false);
  }

  public toggleSignInModal(visible: boolean) {
    this.$services.ui.setModalVisibility('signIn', visible);
    this.$services.ui.firstFocus();
  }

  // Send queued analytics events after Adobe Data Layer has become available.
  emitAdobeAnalyticsEventsAfterAdobeLaunchScriptHasLoaded(): void {
    this.$services.adobeTarget.retrieveUtmParameters(this.$route.query);
    const adobeDataLayerInterval = window.setInterval(() => {
      if(window.adobeDataLayer && this.isLoggedIn != undefined) {
        window.targetVm.cmgApp = this;
        if (this.isLoggedIn) { // 'Auto logged in' event
          this.$services.analytics.send(new LoginCompleteEvent());
        }
       
        this.$services.analytics.emitQueuedAnalyticsEvents();
        clearInterval(adobeDataLayerInterval);
      }
    }, 200);
  }

  showToastr() {
    this.$services.ui.setToastVisibility(true);
  }

  showSignInSignUpExperienceA() {
    this.$services.ui.setSignInSignUpVisibilityExperienceA(true);
    this.$services.ui.setSignInSignUpVisibilityExperienceB(false);
  }

  showSignInSignUpExperienceB() {
    this.$services.ui.setSignInSignUpVisibilityExperienceB(true);
    this.$services.ui.setSignInSignUpVisibilityExperienceA(false);
  }

  showMobileBuilderNavExperienceA() {
    this.$services.ui.setMobileBuilderNavExperienceA(true);
    this.$services.ui.setMobileBuilderNavExperienceB(false);
  }

  showMobileBuilderNavExperienceB() {
    this.$services.ui.setMobileBuilderNavExperienceA(false);
    this.$services.ui.setMobileBuilderNavExperienceB(true);
	}

  adobeTargetMetadataInject(metadataFragment: any) {
    this.$services.metadata.injectMetadata(metadataFragment);
  }

  adobeTargetUpsellItemsInject(upsellItemsPickup: UpsellItem[], upsellItemsDelivery: UpsellItem[]) {
    this.$services.config.setDynamicUpsellItems(upsellItemsPickup, upsellItemsDelivery);
  }

  // TODO: Remove once decision-scopes are in use for Adobe Target. This is 
  // here for backwards compatibility. This is the old way we used to render
  // Target view with at.js
  adobeTargetCMSInject(AEMContentFragment: any, fieldName: string) {
    this.$services.adobeTarget.setPromoBannerValues(AEMContentFragment, fieldName);
  }

  private setMetadataCache(): void {
    const cachedMetadata = this.$appStore.state.menu.cachedMetadata;

    const desktopMetadata = cachedMetadata[MENU_METADATA_CHANNELS.DESKTOP];
    const mobileMetadata = cachedMetadata[MENU_METADATA_CHANNELS.MOBILE];

    // Get cached metadata and etag based on metadata channel
    const metadata = isDesktop(this.$appStore.state) ? desktopMetadata.data : mobileMetadata.data;
    const etag = isDesktop(this.$appStore.state) ? desktopMetadata.etag : mobileMetadata.etag;

    if (!metadata) {
      this.refreshMenuMetadata(true);

      // Only set metadata from cache if changing from desktop to mobile or vice versa.
    } else if (metadata != this.$appStore.state.menu.metadata) {
      this.$services.metadata.setMenuMetadata(metadata, etag);
    }
  }

  private async restoreCustomerState() {

    if (!this.$appStore.state.customer.jwt) {
      this.$services.order.resetPendingOrders();
      return;
    }

    try {
      await this.$services.customer.refreshCustomerProfileInfo(false, true);

      if (this.isLoggedIn && this.$route.name === Routes.CreateAccount) {
        this.$router.push({ name: Routes.EditAccount });
      }

      if (this.customer && !this.customer.isGuest) {
        const label = this.$services.loyalty.isEnrolledInLoyalty(this.customer) ? AnalyticsEventLabel.RewardsEnrolled : AnalyticsEventLabel.RewardsNotEnrolled;
        const optOutValue = document.cookie.split('; ').find(row => row.startsWith('opt-out'));
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.Customer,
          action: AnalyticsEventAction.ReAuthenticated,
          label,
          values: { customerEmail: optOutValue ? null : this.customer.email },
        });
      }

      this.popRepromptCountryProfileModal();

      await this.popExplainerModal();
    } catch (e) {
      this.$services.logger.error('Something went wrong while re-hydrating user info: ', undefined, e);

      this.$services.order.resetPendingOrders();
    }
  }

  private async popExplainerModal(): Promise<void> {
    
    // If the customer has not yet selected a country in profile yet, we will pop the explainer modal after forcing customer to choose country if still applicable
    // If the customer is a non-guest who hasn't enrolled in loyalty and they haven't opted out yet and we are in the US country view experience, pop the explainer modal
    // We won't pop modal for Canadians who are not eligible for joining loyalty
    // If the customer is enrolled in loyalty and has not yet seen the rewards general onboarding 2.0, show the RewardsGeneralOnboarding modal
    const countryProfile = getCountryByHostname(window.location.hostname);

    if (this.customer && !this.isVendorPage) {
      const { isGuest, country } = this.customer;
      const isEnrolledInLoyalty = this.$services.loyalty.isEnrolledInLoyalty(this.customer);
      const hasNotDeclinedLoyalty = !this.$appStore.state.customer.loyalty.declined;

      if (!isGuest && !!country && country != 'CA' && countryProfile.countryCode == 'US') {
        // Don't show this if they have just created an account
        if (isEnrolledInLoyalty && !this.customer.hasSeenRewardsGenOnboarding && !this.$appStore.state.customer.newAccount) {
          this.$services.ui.showOnboardingModal(true, this.$services.customer, this.customer);
        } else if (!isEnrolledInLoyalty && hasNotDeclinedLoyalty) {
          this.$services.ui.setLastSelectedElement('customer-greeting');
          this.$services.ui.setModalVisibility('loyaltyExplainer', true, {
            state: LoyaltyExplainerModalState.WelcomeUnenrolled,
          });
          this.$services.ui.firstFocus();
        }
      }
    }
  }

  private popRepromptCountryProfileModal(): void {
    if (this.customer && !this.customer.isGuest && !this.customer.country && !this.isVendorPage) {
      this.$services.ui.setModalVisibility('repromptCountryProfileModal', true);
    }
  }

  private popGuacModeModal(): void {
    const path = this.$route.path;
    if (path.includes(`/guacmodeverified`) && (this.$services.config.getGuacMode()?.message || '').toLowerCase() === 'true') {
      this.$services.ui.setModalVisibility('guacModeSignIn', true);
      this.$services.analytics.send(new AccountPageLoadEvent(PageLoadEventType.GuacModeNewAccount));
    }
  }

  private popFAC(): void {
    const facString = 'find-a-chipotle';
    const path = this.$route.path;
    if (path.includes(`/${facString}.html`)) {
      // Maintain support for old code
      // e.g. /find-a-chipotle.html/latLng=33.617871824824256,-117.87175777451856 or /find-a-chipotle.html/query=Irvine, CA 92614, USA
      const searchString = path.split('=')[1];
      this.$services.ui.setModalVisibility('findAChipotle', true, { mode: 'pickup', pickupSearchInput: searchString });
      this.$router.replace({ name: Routes.Order, hash: '#menu' });
    } else if (path.includes(`${facString}`)) {
      // e.g. /order/find-a-chipotle or /find-a-chipotle or /find-a-chipotle?mode=delivery&search=610 Newport Center Dr, Newport Beach, CA 92660
      const queryParams = this.$route.query;
      const modalOptions =
        queryParams.mode == 'delivery'
          ? { mode: 'delivery', deliverySearchInput: queryParams.search as string }
          : { mode: 'pickup', pickupSearchInput: queryParams.search as string };
      this.$services.ui.setModalVisibility('findAChipotle', true, modalOptions);
      this.$router.replace({ name: Routes.Order, hash: '#menu' });
    }
  }

  private async joinRewards(): Promise<void> {
    const queryParams = this.$route.query;
    if (queryParams.joinRewards == 'true') {
      this.$router.replace({ query: omit(queryParams, 'joinRewards') });
      await this.$services.customer.waitForCustomerInitialization();
      const enrollmentOptions: EnrollmentOptions = { customer: this.customer, pageTitle: document.title };
      await this.$services.loyalty.enrollInLoyalty(enrollmentOptions);
      this.$services.ui.setLastSelectedElement('customer-greeting');
      this.$services.ui.showOnboardingModal(false, this.$services.customer, this.customer ?? undefined);
    }
  }

  private handleSpecialRoutes(): void {
    this.popFAC();
    this.joinRewards();
    this.popGuacModeModal();
  }

  private async selectRestaurantByURL() {
    const promises: Promise<any>[] = [this.refreshActiveMenu(), this.refreshMenuMetadata(), this.restoreCustomerState(), this.refreshFeatureFlags()];
    const restaurantNumber = parseInt(this.$route.query['restaurant'] as string, 10);
    const hideToast = this.$route.query['hideToast'] == 'true';
    const fromFac = this.$route.query['fromFac'] == 'true';
    const urlHash = fromFac ? '#menu' : this.$route.hash;

    this.$router.replace({ query: omit(this.$route.query, ['hideToast', 'restaurant', 'fromFac']), hash: urlHash });
    if (!isNaN(restaurantNumber)) {
      promises.push(this.$services.restaurant.getRestaurantByNumber(restaurantNumber, [
        RestaurantEmbeds.Addresses,
        RestaurantEmbeds.RealHours,
        RestaurantEmbeds.Experience,
        RestaurantEmbeds.Sustainability
      ]));
    }

    const promiseResponses = await Promise.all(promises);

    // Either load the restaurant from query param, or customer's most recently ordered from restaurant (if the restaurant hasn't already been set)
    if (!isNaN(restaurantNumber)) {
      const restaurant = promiseResponses[promiseResponses.length - 1];
      this.$services.order.setDeliveryAddress(null);
      await this.$services.restaurant.selectRestaurant(restaurant, true);

      this.$services.adobeTarget.sendAdobeTargetExperienceRequest(this.$route.name);

      if (!hideToast) {
        this.$services.ui.setModalVisibility('toast', true, {
          title: 'PICKING UP FROM',
          subTitle: (this.$services.customer.getFavoriteRestaurantName(restaurant.restaurantNumber) || '').toUpperCase(),
          body: this.$services.customer.getFavoriteRestaurantName(restaurant.restaurantNumber)
            ? restaurant.address.addressLine1
            : `${restaurant.address.addressLine1}, ${restaurant.address.locality}, ${restaurant.address.administrativeArea} ${
                (restaurant.address.postalCode || '').split('-')[0]
              }`,
        });
      }
    }
  }

  private setBodyScroll() {
    if (this.$appStore.state.ui.app.scrollable && !document.querySelector('.bag-container.fullModal[visible="visible"]')) {
      // Check if there are other modals open before removing not-scrollable class
      document.body.classList.remove('not-scrollable');
    } else {
      document.body.classList.add('not-scrollable');
    }
  }

  private async onUserLogin() {
    if (!this.$appStore.state.customer.jwt) {
      return;
    }

    this.$services.adobeTarget.sendAdobeTargetExperienceRequest(undefined, USER_PROFILE_DECISION_SCOPES);
    this.$services.logger.debug('Refreshing data after user login...');

    const hasBag = this.$route.query.hasBag == 'true';
    const urlHash = this.getUrlHash();
    this.$router.replace({ query: omit(this.$route.query, 'hasBag'), hash: urlHash });
    if (!hasBag) {
      this.refreshPendingOrders();
    }

    this.popRepromptCountryProfileModal();

    await this.popExplainerModal();

    // call refresh payment method, favorites, promos and points only if not a new user
    if (!this.$appStore.state.customer.newAccount) {
      this.refreshPaymentMethods();

      this.refreshFavoriteOrders();

      this.refreshCustomerPromotions();

      this.checkForEarnedPointsAndRewards();
    }
  }

  private checkForEarnedPointsAndRewards() {
    if (!this.isRewardsOnline || !this.isRewardsExchangeOnline) {
      return;
    }

    const lastUpdated = this.$appStore.state.customer.celebration.lastUpdated;
    if (lastUpdated && moment(lastUpdated).isSame(moment(), 'day')) {
      return;
    }

    this.$services.customer.waitForCustomerInitialization().then(async isLoggedIn => {
      if (!isLoggedIn || !this.$services.loyalty.isLoyaltyEligible(this.$appStore.state.customer.profile) || !this.$services.loyalty.isEnrolledInLoyalty(this.$appStore.state.customer.profile)) {
        return;
      }

      const promises = await Promise.all([this.$services.promotions.refreshCustomerPromotions(), this.$services.loyalty.refreshPoints()]);
      const promos = promises[0];
      const points = promises[1];

      // because of potentially long delays in getting data above, the customer may have moved on to another page
      // that shouldn't actually show celebrations
      if (this.$route.name != Routes.Home && this.$route.name != Routes.Order) {
        return;
      }

      const newRewardCount = promos.length - this.$appStore.state.customer.celebration.numberOfRewards;
      if (newRewardCount > 0) {
        this.$services.ui.setModalVisibility('rewardsCelebration', true, { newRewardCount });
        this.$nextTick(() => this.$services.ui.focusDiv('.cmg-modal-container'));
      } else if (points && points.currentPointsBalance > this.$appStore.state.customer.celebration.points) {
        this.$services.ui.setPointsCelebrationVisibility(true);
      }

      this.$services.customer.setCelebrationData(promos.length, points.currentPointsBalance);
    });
  }

  private refreshCustomerPromotions() {
    try {
      return this.$services.promotions.refreshCustomerPromotions();
    } catch (e) {
      this.$services.logger.error('Something went wrong while fetching customer promotions: ', e);
    }
  }

  private async refreshFavoriteOrders(): Promise<any> {
    if (this.isLoadingFavorites) {
      return;
    }
    this.isLoadingFavorites = true;

    try {
      await this.$services.recentAndFavoriteOrders.refreshFavoriteOrders();
    } catch (e) {
      this.$services.logger.error('Something went wrong while refreshing favorite orders', undefined, e);
    } finally {
      this.isLoadingFavorites = false;
    }
  }

  private async onRestaurantSelected() {
    this.$services.logger.debug('Refreshing data after restaurant selection...');

    this.refreshActiveMenu();
  }

  private async onFetchMostRecentRestaurant() {
    const mostRecentRestaurant = this.$appStore.state.restaurant.mostRecentRestaurant;
    if (!this.$appStore.state.restaurant.selectedRestaurant && mostRecentRestaurant && mostRecentRestaurant.address.countryCode == this.siteCountryCode) {
      this.$services.restaurant.selectRestaurant(mostRecentRestaurant, this.$services.order.isPickingUp());
    }
  }

  private async refreshMenuMetadata(forceRefresh: boolean = false) {
    return await this.$services.metadata.refreshMenuMetadata(forceRefresh);
  }

  private async refreshActiveMenu() {
    return await this.$services.menu.refreshActiveMenu();
  }

  private async refreshPaymentMethods() {
    await this.$services.transaction.refreshPaymentCards();

    if (this.giftCard) {
      const giftCardBalance = this.$appStore.state.customer.wallet.giftCardBalances[this.giftCard.tokenizedAccountNumber];

      if (!giftCardBalance) {
        await this.$services.transaction.getGiftCardBalance(this.giftCard.tokenizedAccountNumber);
      }
    }
  }

  private async refreshPCEs() {
    return await this.$services.menu.refreshPreConfiguredMeals();
  }

  private async refreshPendingOrders() {
    return await this.$services.order.refreshPendingOrders();
  }

  private async refreshFeatureFlags() {
    return await this.$services.featureFlags.refresh();
  }

  private async handleCrossDomainDataMessage(event: any, win: Window) {
    const eventData = event.data && (JSON.parse(event.data) as PersistCrossDomainData);

    await this.handleCrossDomainJwt(eventData, win);

    const promises: Promise<string | void>[] = [];

    if (eventData) {
      if (eventData.deliveryAddress) {
        promises.push(this.handleCrossDomainDeliveryAddress(eventData.deliveryAddress));
      }

      if (eventData.order) {
        const order = await this.$services.order.createOrder(eventData.order.order.restaurantId);

        if (order && eventData.order.order.meals) {
          for (const m of eventData.order.order.meals) {
            if (m.preconfiguredMealId) {
              promises.push(this.$services.order.addPreconfiguredMealToOrder(order, m.preconfiguredMealId, m.mealName));
            } else {
              const entrees = m.entrees as OrderMealRequestEntree[];
              const { mealName, sides, drinks } = m;
              const meal = { mealName, entrees, sides, drinks };
              promises.push(this.$services.order.addMealToOrder(order, { meal }));
            }
          }

          if (eventData.order.order.nonFoodItems) {
            for (const i of eventData.order.order.nonFoodItems) {
              promises.push(this.$services.order.addNonFoodItemToOrder(order, { menuItemId: i.menuItemId, quantity: 1, isUpSell: false }));
            }
          }

          await Promise.all(promises);
          await this.refreshPendingOrders();
        }
      }

      win.postMessage(JSON.stringify({ key: 'cmg-vuex', method: 'set', data: { jwt: eventData.jwt } }), this.$services.config.getSessionFrameUrl());
    }
  }

  private async handleCrossDomainJwt(eventData: PersistCrossDomainData, win: Window) {
    // If logged in and no jwt returned from the session iframe clear out user session
    if (!eventData || !eventData.jwt) {
      // Only update customer in store (with force guest) if TLD is different. This gets triggered on new pageloads as well.
      if (this.customer && this.customer.country != getCountryCodeByTopLevelDomain()) {
        this.$services.customer.setProfile({ ...this.customer, isGuest: true });
      }
      if (win) {
        win.postMessage(JSON.stringify({ key: 'cmg-vuex', method: 'remove' }), this.$services.config.getSessionFrameUrl());
      }
    } else if (eventData && eventData.jwt) {
      if (!this.$appStore.state.customer.jwt) {
        // Persist the jwt returned from the session iframe
        this.$services.customer.setJwt(eventData.jwt);
      }
      await this.restoreCustomerState();
    }
  }

  private async handleCrossDomainDeliveryAddress(deliveryAddress: CustomerDeliveryAddress) {
    const validationResult = await this.$services.order.validateDeliveryAddress(this.$services.findAChipotle.customerDeliveryAddressToValidationRequest(deliveryAddress));
    if (validationResult && !(validationResult instanceof DeliveryApiException) && validationResult.statusText === 'OK') {
      // If we were able to get a restaurantId we can use that to set the current restaurant
      const restaurantId = validationResult.data.restaurantId;
      if (restaurantId) {
        const restaurant = await this.$services.restaurant.getRestaurantByNumber(restaurantId);
        await this.$services.restaurant.selectRestaurant(restaurant, false);
      } else {
        await this.$services.restaurant.selectRestaurant(null, false);
      }

      // set local selected address before selecting restaurant to ensure we have all the info we need to recalculate things like estimates
      await this.$services.order.setDeliveryAddress(deliveryAddress);
      this.$services.checkout.setDeliveryInstructions(deliveryAddress.notes || '');

      if (this.$route.query.showDeliveryToast == 'true') {
        this.$router.replace({ query: omit(this.$route.query, 'showDeliveryToast') });
        this.showDeliveringToModal(deliveryAddress, restaurantId);
      }

      // Plop phone number and name into store so we can carry over address-specific data to checkout
      this.$services.checkout.setDeliveryPhoneNumber(deliveryAddress.phoneNumber);
      this.$services.checkout.setDeliveryName(deliveryAddress.customerName);
    }
  }

  private showDeliveringToModal(deliveryAddress: CustomerDeliveryAddress, restaurantId: number) {
    this.$services.ui.setModalVisibility('toast', true, {
      title: 'DELIVERING TO',
      subTitle: deliveryAddress.name ? deliveryAddress.name.toUpperCase() : undefined,
      body: deliveryAddress.streetAddress1!,
      legalCopy: 'Menu pricing for delivery is higher and fees apply.',
      icon: this.$appStore.state.cms.images.icons.carWhite.default,
      after: async () => {
        const epops = await this.$services.epop.getEpopsForRestaurant(restaurantId);
        if (epops && epops.length > 0) {
          this.$services.ui.setModalVisibility('epop', true, {
            epops,
          });
          this.$services.ui.firstFocus();
        }
      },
    });
  }

  private getUrlHash() {
    let hash = '';
    if (this.$route.hash) {
      hash = this.$route.hash.indexOf('?') > -1 ? this.$route.hash.substring(0, this.$route.hash.indexOf('?') - 1) : this.$route.hash;
    }
    return hash;
  }

  private resetMenuAndMetadataCachesOnce() {
    const hasResetCache = !!localStorage.getItem('hasResetMenuAndMetadataCaches');

    if (!hasResetCache) {
      localStorage.setItem('hasResetMenuAndMetadataCaches', 'true');

      const cachedMenuRestaurantIds = this.$store.state.menu.cachedMenuRestaurantIds;

      while (cachedMenuRestaurantIds.length) {
        this.$store.commit(MENU_MUTATIONS.REMOVE_CACHED_MENU_FOR_RESTAURANT, cachedMenuRestaurantIds.pop());
      }

      this.$services.metadata.setMenuMetadata();
    }
  }

  private setTitleAndDescription(): void {
    if( this.$appStore.state.cms.home.title ) {
      document.title = this.$appStore.state.cms.home.title;
    }
    if(this.$appStore.state.cms.home.description) {
      const descriptionMetaTag = document.createElement('meta');
      descriptionMetaTag.setAttribute('name', 'description');
      descriptionMetaTag.setAttribute('content', this.$appStore.state.cms.home.description);
      document.getElementsByTagName('head')[0].appendChild(descriptionMetaTag);
    }
  }

  get isRewardsExchangeOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsExchangeOnline ?? true;
  }

  get isRewardsOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsOnline ?? true;
  }
}
